import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';

@Injectable({
  providedIn: 'root'
})
export class LanguageService {
  private readonly STORAGE_KEY = 'portal_lang';
  private readonly SUPPORTED_LANGUAGES = ['es', 'en'];
  private readonly DEFAULT_LANGUAGE = 'es';

  constructor(private translate: TranslateService) {}

  /**
   * Detecta el idioma del navegador del usuario
   * @returns Código de idioma detectado (es, en)
   */
  detectBrowserLanguage(): string {
    const browserLang = navigator.language.split('-')[0]; // ej: 'es-ES' -> 'es'
    return this.SUPPORTED_LANGUAGES.includes(browserLang) ? browserLang : this.DEFAULT_LANGUAGE;
  }

  /**
   * Obtiene el idioma guardado en localStorage
   * @returns Idioma guardado o null si no existe
   */
  getStoredLanguage(): string | null {
    return localStorage.getItem(this.STORAGE_KEY);
  }

  /**
   * Guarda el idioma seleccionado en localStorage y actualiza la app
   * @param lang Código de idioma (es, en)
   */
  setLanguage(lang: string): void {
    if (this.SUPPORTED_LANGUAGES.includes(lang)) {
      localStorage.setItem(this.STORAGE_KEY, lang);
      this.translate.use(lang);
    }
  }

  /**
   * Obtiene el idioma actualmente en uso
   * @returns Código de idioma actual
   */
  getCurrentLanguage(): string {
    return this.translate.currentLang || this.DEFAULT_LANGUAGE;
  }

  /**
   * Inicializa el sistema de idiomas al cargar la aplicación
   * Prioridad: localStorage -> navegador -> español
   */
  initLanguage(): void {
    const storedLang = this.getStoredLanguage();
    const browserLang = this.detectBrowserLanguage();
    
    // Si el usuario ya seleccionó un idioma manualmente, usar ese
    // Si no, usar el del navegador pero SIN guardarlo
    const languageToUse = storedLang || browserLang;

    console.log('🌐 [LanguageService] Inicializando sistema de idiomas...');
    console.log('🌐 [LanguageService] Idioma del navegador:', browserLang);
    console.log('🌐 [LanguageService] Idioma guardado (selección manual):', storedLang);
    console.log('🌐 [LanguageService] Idioma a usar:', languageToUse);

    // Establecer idioma por defecto
    this.translate.setDefaultLang(this.DEFAULT_LANGUAGE);
    
    // Usar el idioma detectado/guardado
    this.translate.use(languageToUse).subscribe({
      next: (translations) => {
        console.log('✅ [LanguageService] Traducciones cargadas:', translations);
      },
      error: (err) => {
        console.error('❌ [LanguageService] Error cargando traducciones:', err);
      }
    });

    console.log('✅ [LanguageService] Sistema de idiomas inicializado correctamente');
  }

  /**
   * Obtiene la lista de idiomas soportados
   * @returns Array con códigos de idiomas soportados
   */
  getSupportedLanguages(): string[] {
    return [...this.SUPPORTED_LANGUAGES];
  }
}
